import React, { useCallback, useState } from 'react';
import ReactCrop from 'react-image-crop';
import { ImagePreviewProps } from '../utils/types';
import 'react-image-crop/dist/ReactCrop.css';

const ImagePreview: React.FC<ImagePreviewProps> = ({
  imgSrc,
  processedImage,
  processedSize,
  operationMode,
  cropProps,
  imgRef,
  onFileSelect,
}) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      onFileSelect(file);
    }
  }, [onFileSelect]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileSelect(file);
    }
  }, [onFileSelect]);

  const renderImageUploadArea = () => (
    <div
      className={`w-full h-full flex flex-col items-center justify-center border-2 border-dashed rounded-lg transition-colors cursor-pointer
        ${isDragging 
          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
          : 'border-gray-300 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400'
        }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => document.getElementById('file-input')?.click()}
    >
      <input
        id="file-input"
        type="file"
        accept="image/*"
        onChange={handleFileInput}
        className="hidden"
      />
      <svg
        className={`w-12 h-12 mb-3 ${isDragging ? 'text-blue-500' : 'text-gray-400 dark:text-gray-500'}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        {isDragging ? 'Drop your image here' : 'Click or drag image to upload'}
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
        Supports JPG, PNG, GIF
      </p>
    </div>
  );

  const renderOriginalImage = () => {
    if (!imgSrc) return renderImageUploadArea();

    if (operationMode === 'crop' && cropProps) {
      return (
        <div className="w-full h-full flex items-center justify-center">
          <ReactCrop
            crop={cropProps.crop}
            onChange={cropProps.setCrop}
            onComplete={cropProps.onComplete}
            aspect={cropProps.aspect}
            className="max-w-[90%] max-h-[90%]"
          >
            <img
              ref={imgRef}
              src={imgSrc}
              alt="Original"
              onLoad={cropProps.onImageLoad}
              style={{ transform: `scale(${cropProps.scale})` }}
              className="max-w-full max-h-[70vh] object-contain"
            />
          </ReactCrop>
        </div>
      );
    }

    return (
      <div className="w-full h-full flex items-center justify-center">
        <img
          ref={imgRef}
          src={imgSrc}
          alt="Original"
          className="max-w-[90%] max-h-[70vh] object-contain"
        />
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-grow min-h-[60vh]">
      {/* Original Image */}
      <div className="flex flex-col h-full">
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 flex-grow flex items-center justify-center overflow-auto min-h-[50vh]">
          {renderOriginalImage()}
        </div>
      </div>

      {/* Processed Image */}
      <div className="flex flex-col h-full">
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 flex-grow flex items-center justify-center overflow-auto min-h-[50vh]">
          {processedImage ? (
            <div className="flex flex-col items-center w-full h-full">
              <div className="w-full h-full flex items-center justify-center">
                <img
                  src={processedImage}
                  alt="Processed"
                  className="max-w-[90%] max-h-[70vh] object-contain mb-2"
                />
              </div>
              <span className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                Size: {(processedSize / 1024).toFixed(2)} KB
              </span>
            </div>
          ) : (
            <div className="text-gray-500 dark:text-gray-400 text-center">
              {operationMode === 'crop' 
                ? 'Cropped image will appear here'
                : 'Compressed image will appear here'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImagePreview; 